import React, { useEffect, useRef, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  ScatterController,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";

ChartJS.register(ScatterController, LinearScale, PointElement, LineElement, Filler);

// 🔥 DARK Saturated lines that pop out nicely against the quadrants 🔥
const DARK_LINE_COLORS = {
  leading: '#047857',   // emerald-700
  improving: '#0369a1', // sky-700
  lagging: '#be123c',   // rose-700
  weakening: '#b45309', // amber-700
  unassigned: '#475569' // slate-600
};

// Plugin: Quadrants using injected palette
const quadrantPlugin = (bgColors) => ({
  id: "quadrants",
  beforeDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!scales.x || !scales.y) return;
    const { left, right, top, bottom } = chartArea;
    const cx = scales.x.getPixelForValue(100);
    const cy = scales.y.getPixelForValue(100);

    ctx.save();
    const quadrants = [
      { x: cx, y: top, w: right - cx, h: cy - top, c: bgColors.leading, l: 'LEADING', tx: right - 10, ty: top + 15, a: 'right' },
      { x: left, y: top, w: cx - left, h: cy - top, c: bgColors.improving, l: 'IMPROVING', tx: left + 8, ty: top + 15, a: 'left' },
      { x: left, y: cy, w: cx - left, h: bottom - cy, c: bgColors.lagging, l: 'LAGGING', tx: left + 8, ty: bottom - 8, a: 'left' },
      { x: cx, y: cy, w: right - cx, h: bottom - cy, c: bgColors.weakening, l: 'WEAKENING', tx: right - 10, ty: bottom - 8, a: 'right' },
    ];

    quadrants.forEach((q) => {
      if (q.w > 0 && q.h > 0) {
        ctx.fillStyle = q.c;
        ctx.fillRect(q.x, q.y, q.w, q.h);
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.font = "800 11px -apple-system, sans-serif";
        ctx.textAlign = q.a;
        ctx.fillText(q.l, q.tx, q.ty);
      }
    });

    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 5]);
    ctx.beginPath(); ctx.moveTo(cx, top); ctx.lineTo(cx, bottom); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(left, cy); ctx.lineTo(right, cy); ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  },
});

// Plugin: Arrow Head ONLY (No circle dot underneath)
const labelAndArrowPlugin = (showLabels, hoveredSymbol) => ({
  id: "labelsAndArrows",
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    if (!scales.x || !scales.y) return;

    ctx.save();
    data.datasets.forEach((ds) => {
      if (!ds.data || ds.data.length < 2) return;
      const points = ds.data;
      const last = points[points.length - 1];
      const prev = points[points.length - 2];
      
      const px = scales.x.getPixelForValue(last.x);
      const py = scales.y.getPixelForValue(last.y);
      const pxPrev = scales.x.getPixelForValue(prev.x);
      const pyPrev = scales.y.getPixelForValue(prev.y);

      // Draw exactly ONE pure arrow pointing to the destination
      const angle = Math.atan2(py - pyPrev, px - pxPrev);
      const isHovered = hoveredSymbol === ds.label;
      const arrowSize = isHovered ? 8 : 6;
      
      ctx.fillStyle = ds.borderColor;
      ctx.beginPath();
      ctx.moveTo(px, py); // Tip is exactly at the final point
      ctx.lineTo(px - Math.cos(angle - 0.45) * arrowSize * 2, py - Math.sin(angle - 0.45) * arrowSize * 2);
      ctx.lineTo(px - Math.cos(angle + 0.45) * arrowSize * 2, py - Math.sin(angle + 0.45) * arrowSize * 2);
      ctx.closePath();
      ctx.fill();

      if (showLabels) {
        ctx.fillStyle = ds.borderColor;
        ctx.font = isHovered ? "900 12px -apple-system, sans-serif" : "700 10px -apple-system, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(ds.label, px + 8, py + 4);
      }
    });
    ctx.restore();
  },
});

const RrgChart = ({
  data = [],
  showLabels = true,
  viewMode = "fit",
  playStep = null,
  playSpeed = 450,
  playing = false,
  hoveredSymbol,
  setHoveredSymbol,
  CHART_QUAD_BGS = {}
}) => {
  const chartRef = useRef(null);
  const [tooltipState, setTooltipState] = useState(null);

  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    if (!data.length) return { xMin: 90, xMax: 110, yMin: 94, yMax: 106 };
    let allX = [], allY = [];
    data.forEach((d) => {
      d.trail.forEach((p) => {
        if (p.rs_ratio != null && p.rs_momentum != null) { allX.push(p.rs_ratio); allY.push(p.rs_momentum); }
      });
    });
    if (allX.length === 0) return { xMin: 90, xMax: 110, yMin: 94, yMax: 106 };

    let minX = Math.min(...allX), maxX = Math.max(...allX);
    let minY = Math.min(...allY), maxY = Math.max(...allY);

    if (viewMode === "center") {
      const r = Math.max(Math.abs(maxX - 100), Math.abs(minX - 100), 2.5);
      const ry = Math.max(Math.abs(maxY - 100), Math.abs(minY - 100), 2.5);
      return { xMin: 100 - r, xMax: 100 + r, yMin: 100 - ry, yMax: 100 + ry };
    }
    if (viewMode === "max") return { xMin: 94, xMax: 106, yMin: 94, yMax: 106 };
    const padX = Math.max(0.4, (maxX - minX) * 0.20 + 0.4);
    const padY = Math.max(0.4, (maxY - minY) * 0.20 + 0.4);
    return { xMin: minX - padX, xMax: maxX + padX, yMin: minY - padY, yMax: maxY + padY };
  }, [data, viewMode]);

  const datasets = useMemo(() => {
    return data.map((d) => {
      const trail = d.trail.filter(p => p && p.rs_ratio != null && p.rs_momentum != null && p.rs_ratio > 20 && p.rs_momentum > 20);
      if (trail.length < 2) return null;

      // Use Dark Saturated colors for snakes
      const color = DARK_LINE_COLORS[d.quadrant] || DARK_LINE_COLORS.unassigned;
      const isHovered = hoveredSymbol === d.symbol;
      
      const points = trail.map((p) => ({ 
          x: p.rs_ratio, 
          y: p.rs_momentum, 
          rawDate: p.date, 
          rawPrice: p.price || p.close,
          rsi: p.rsi_14,
          ema: p.ema_10,
          sma: p.sma_10,
          st: p.supertrend
      }));
      
      const len = points.length;
      const lastIndex = len - 1;

      return {
        label: d.symbol,
        data: points,
        borderColor: color,
        backgroundColor: color,
        borderWidth: isHovered ? 3.5 : 2.5, // Strong line width
        
        // Ensure the last dot has 0 radius so ONLY the plugin arrow draws
        pointRadius: points.map((_, i) => i === lastIndex ? 0 : Math.max(3.5, 5 * (i + 1) / len)),
        pointHoverRadius: points.map((_, i) => i === lastIndex ? 0 : 7),
        
        pointBackgroundColor: color,
        pointBorderColor: "transparent",
        pointBorderWidth: 0,
        tension: 0.4,
        fill: false,
        showLine: true,
        order: isHovered ? 1 : 0,
      };
    }).filter(Boolean);
  }, [data, hoveredSymbol]);

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: playing ? Math.round(playSpeed * 0.3) : 300,
        easing: "linear",
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false }, 
      },
      scales: {
        x: {
          type: "linear", min: xMin, max: xMax,
          grid: { color: "rgba(0,0,0,0.08)" },
          ticks: { color: "#555", font: { size: 10, weight: "bold" }, callback: (v) => v.toFixed(1) },
          title: { display: true, text: "JDK RS-RATIO  →", color: "#555", font: { size: 11, weight: "800" } },
          border: { color: "#888" },
        },
        y: {
          type: "linear", min: yMin, max: yMax,
          grid: { color: "rgba(0,0,0,0.08)" },
          ticks: { color: "#555", font: { size: 10, weight: "bold" }, callback: (v) => v.toFixed(1), count: 8 },
          title: { display: false },
          border: { color: "#888" },
        },
      },
    };
  }, [xMin, xMax, yMin, yMax, playing, playSpeed]);

  const handleMouseMove = (e) => {
    const rrgInst = chartRef.current;
    if (!rrgInst) return;

    const rect = e.target.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    
    let found = null, minD = 20; 
    
    rrgInst.data.datasets.forEach(ds => {
      if (!ds.data || !ds.data.length) return;
      ds.data.forEach(pt => {
        const px = rrgInst.scales.x.getPixelForValue(pt.x);
        const py = rrgInst.scales.y.getPixelForValue(pt.y);
        const d = Math.hypot(mx - px, my - py);
        if (d < minD) { minD = d; found = { ds, pt }; }
      });
    });

    if (found) {
      const symData = data.find(d => d.symbol === found.ds.label);
      const qColor = found.ds.borderColor;
      
      let dynamicQ = "-";
      if (found.pt.x >= 100 && found.pt.y >= 100) dynamicQ = "Leading";
      else if (found.pt.x < 100 && found.pt.y >= 100) dynamicQ = "Improving";
      else if (found.pt.x < 100 && found.pt.y < 100) dynamicQ = "Lagging";
      else if (found.pt.x >= 100 && found.pt.y < 100) dynamicQ = "Weakening";

      let p = found.pt.rawPrice || 0;
      let dateLabel = found.pt.rawDate || "N/A";
      
      let chg = 0;
      if (p && symData?.trail?.length > 1) {
        const idx = symData.trail.findIndex(tr => tr.date === dateLabel);
        if (idx > 0) {
           const prev = symData.trail[idx - 1].close || symData.trail[idx - 1].price || p;
           chg = ((p - prev) / prev) * 100;
        }
      }

      setTooltipState({
        show: true, x: e.clientX + 14, y: e.clientY - 10,
        name: found.ds.label, date: dateLabel, color: qColor,
        rsRatio: found.pt.x.toFixed(2), rsMom: found.pt.y.toFixed(2),
        quadrant: dynamicQ, 
        price: p > 0 ? (p >= 10000 ? (p/1000).toFixed(1)+'k' : p.toFixed(2)) : '—',
        change: p > 0 ? `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}%` : '—',
        isPos: chg >= 0,
        rsi: found.pt.rsi ? found.pt.rsi.toFixed(2) : '-',
        ema: found.pt.ema ? found.pt.ema.toFixed(1) : '-',
        sma: found.pt.sma ? found.pt.sma.toFixed(1) : '-',
        st: found.pt.st ? found.pt.st.toFixed(1) : '-',
        isStBuy: found.pt.st > 0
      });
      setHoveredSymbol?.(found.ds.label);
    } else {
      setTooltipState(null);
      setHoveredSymbol?.(null);
    }
  };

  return (
    <div className="w-full h-full bg-transparent relative">
      <Chart
        ref={chartRef}
        type="scatter"
        data={{ datasets }}
        options={options}
        plugins={[quadrantPlugin(CHART_QUAD_BGS), labelAndArrowPlugin(showLabels, hoveredSymbol)]}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => { setTooltipState(null); setHoveredSymbol?.(null); }}
      />

      {tooltipState && tooltipState.show && (
        <div style={{ position: 'fixed', left: tooltipState.x, top: tooltipState.y, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', padding: '8px 12px', fontSize: '11px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', pointerEvents: 'none', zIndex: 9999, minWidth: '165px', fontFamily: '-apple-system, sans-serif' }}>
          <div style={{ fontWeight: 900, fontSize: '13px', marginBottom: '2px', color: tooltipState.color, borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>{tooltipState.name}</div>
          <div style={{ fontWeight: 700, fontSize: '10px', marginBottom: '6px', color: '#64748b' }}>{tooltipState.date}</div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569', marginBottom: '2px' }}><span>Price</span><span style={{ fontWeight: 800, color: '#0369a1' }}>₹{tooltipState.price}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569', marginBottom: '4px' }}><span>Change</span><span style={{ fontWeight: 800, color: tooltipState.isPos ? '#059669' : '#e11d48' }}>{tooltipState.change}</span></div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569' }}><span>RSI</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{tooltipState.rsi}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569' }}><span>EMA / SMA</span><span style={{ fontWeight: 700, color: '#0f172a' }}>{tooltipState.ema} / {tooltipState.sma}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569', marginBottom: '4px' }}><span>Supertrend</span><span style={{ fontWeight: 700, color: tooltipState.isStBuy ? '#059669' : '#e11d48' }}>₹{Math.abs(tooltipState.st)} {tooltipState.isStBuy ? '(Buy)' : '(Sell)'}</span></div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569', marginTop:'4px', borderTop:'1px dashed #cbd5e1', paddingTop:'4px' }}><span>RS-Ratio</span><span style={{ fontWeight: 700, color: '#059669' }}>{tooltipState.rsRatio}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569' }}><span>RS-Mom</span><span style={{ fontWeight: 700, color: '#059669' }}>{tooltipState.rsMom}</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '14px', color: '#475569', marginTop:'2px' }}><span>Quadrant</span><span style={{ fontWeight: 900, color: tooltipState.color }}>{tooltipState.quadrant}</span></div>
        </div>
      )}
    </div>
  );
};

export default RrgChart;